/**
 * Shipping Page Component
 * Multi-carrier shipping rate management with DESI rows and route columns
 * Delegates logic to useShippingData hook and rendering to sub-components
 */

import React from 'react';
import {
  Upload,
  Download,
  Save,
  RefreshCw,
  Plus,
  Truck,
  Route,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useLanguage } from '../../i18n';
import { useShippingData } from './useShippingData';
import SummaryTable from './SummaryTable';
import CarrierRateCard from './CarrierRateCard';
import { useModalA11y } from '../../hooks/useModalA11y';

const ShippingPage: React.FC = () => {
  const { t } = useLanguage();
  const data = useShippingData();

  // Modal a11y refs (Escape + initial focus)
  const addCarrierModalRef = useModalA11y(data.showAddCarrier, () => data.setShowAddCarrier(false));
  const deleteCarrierModalRef = useModalA11y(!!data.deleteConfirm, () => data.setDeleteConfirm(null));
  const addRouteModalRef = useModalA11y(data.showAddRoute, () => data.setShowAddRoute(false));
  const deleteRouteModalRef = useModalA11y(!!data.deleteRouteConfirm, () => data.setDeleteRouteConfirm(null));
  const carrierRoutesModalRef = useModalA11y(!!data.showCarrierRoutes, () => data.setShowCarrierRoutes(null));

  if (data.isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" aria-hidden="true" />
        <span className="sr-only">{t.common.loading}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-600 to-slate-700 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Truck className="w-8 h-8" />
          <h2 className="text-2xl font-bold">{t.shipping.title}</h2>
        </div>
        <p className="opacity-90">{t.shipping.description}</p>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={data.toggleDesiView}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              {data.showAllDesi ? t.shipping.showLess : t.shipping.showAll}
            </button>
            <span className="text-sm text-slate-500">
              {data.visibleDesi.length} {t.shipping.desiColumns}
            </span>
            <span className="text-slate-300">|</span>
            <span className="text-sm text-slate-500">
              {data.carriers.filter(c => c.isActive).length} kargo firmas&#x131;
            </span>
          </div>

          <div className="flex items-center gap-2">
            {data.importStatus && (
              <span className={`text-sm px-3 py-1 rounded-full ${
                data.importStatus.errors > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}>
                {data.importStatus.success > 0 && `${data.importStatus.success} ${t.shipping.saved}`}
                {data.importStatus.errors > 0 && ` ${data.importStatus.errors} ${t.shipping.errors}`}
              </span>
            )}

            {data.hasChanges && (
              <button
                onClick={data.saveAllChanges}
                disabled={data.isSaving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {data.isSaving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {t.common.save}
              </button>
            )}

            <button
              onClick={() => data.setShowAddCarrier(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200"
            >
              <Plus className="w-4 h-4" />
              Firma Ekle
            </button>

            <button
              onClick={() => data.setShowAddRoute(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-emerald-200"
            >
              <Route className="w-4 h-4" />
              Rota Ekle
            </button>

            <input
              ref={data.fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={data.handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => data.fileInputRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
            >
              <Upload className="w-4 h-4" />
              {t.shipping.import}
            </button>

            <button
              onClick={data.exportToExcel}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
            >
              <Download className="w-4 h-4" />
              {t.shipping.export}
            </button>
          </div>
        </div>
      </div>

      {/* Summary Table */}
      <SummaryTable
        routes={data.routes}
        visibleDesi={data.visibleDesi}
        exchangeRates={data.exchangeRates}
        deleteRouteConfirm={data.deleteRouteConfirm}
        setDeleteRouteConfirm={data.setDeleteRouteConfirm}
        getComputedRate={data.getComputedRate}
      />

      {/* Add Carrier Modal */}
      {data.showAddCarrier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            ref={addCarrierModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-carrier-modal-title"
            className="bg-white rounded-xl p-6 w-full max-w-md"
          >
            <h3 id="add-carrier-modal-title" className="text-lg font-semibold mb-4">Yeni Kargo Firmas&#x131;</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Firma Ad&#x131;</label>
                <input
                  type="text"
                  value={data.newCarrier.name}
                  onChange={(e) => data.setNewCarrier(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="DHL Express"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">K&#x131;sa Kod</label>
                <input
                  type="text"
                  value={data.newCarrier.shortName}
                  onChange={(e) => data.setNewCarrier(prev => ({ ...prev, shortName: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="DHL"
                  maxLength={10}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Para Birimi</label>
                  <select
                    value={data.newCarrier.currency}
                    onChange={(e) => data.setNewCarrier(prev => ({ ...prev, currency: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="USD">$ USD</option>
                    <option value="EUR">&#x20AC; EUR</option>
                    <option value="TRY">&#x20BA; TRY</option>
                    <option value="GBP">&#xA3; GBP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Renk</label>
                  <input
                    type="color"
                    value={data.newCarrier.color}
                    onChange={(e) => data.setNewCarrier(prev => ({ ...prev, color: e.target.value }))}
                    className="w-full h-10 rounded-lg cursor-pointer"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => data.setShowAddCarrier(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={data.handleAddCarrier}
                disabled={!data.newCarrier.name || !data.newCarrier.shortName}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Carrier Confirmation Modal */}
      {data.deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            ref={deleteCarrierModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-carrier-modal-title"
            className="bg-white rounded-xl p-6 w-full max-w-md"
          >
            <h3 id="delete-carrier-modal-title" className="text-lg font-semibold mb-4 text-red-600">Firmay&#x131; Sil</h3>
            <p className="text-slate-600 mb-6">
              &quot;{data.carriers.find(c => c.id === data.deleteConfirm)?.name}&quot; firmas&#x131;n&#x131; ve t&#xFC;m tarifelerini silmek istedi&#x11F;inize emin misiniz?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => data.setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                &#x130;ptal
              </button>
              <button
                onClick={() => data.handleDeleteCarrier(data.deleteConfirm!)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Route Modal */}
      {data.showAddRoute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            ref={addRouteModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-route-modal-title"
            className="bg-white rounded-xl p-6 w-full max-w-md"
          >
            <h3 id="add-route-modal-title" className="text-lg font-semibold mb-4">Yeni Rota Ekle</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">&#xC7;&#x131;k&#x131;&#x15F; &#xDC;lkesi</label>
                  <input
                    type="text"
                    value={data.newRoute.origin}
                    onChange={(e) => data.setNewRoute(prev => ({ ...prev, origin: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="TR"
                    maxLength={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hedef &#xDC;lke *</label>
                  <input
                    type="text"
                    value={data.newRoute.destination}
                    onChange={(e) => data.setNewRoute(prev => ({ ...prev, destination: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="BR"
                    maxLength={3}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rota Ad&#x131; *</label>
                <input
                  type="text"
                  value={data.newRoute.name}
                  onChange={(e) => data.setNewRoute(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="TR &#x2192; Brezilya"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">K&#x131;sa Ad *</label>
                  <input
                    type="text"
                    value={data.newRoute.shortName}
                    onChange={(e) => data.setNewRoute(prev => ({ ...prev, shortName: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="BR"
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bayrak</label>
                  <input
                    type="text"
                    value={data.newRoute.flag}
                    onChange={(e) => data.setNewRoute(prev => ({ ...prev, flag: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    maxLength={4}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Para Birimi</label>
                  <input
                    type="text"
                    value={data.newRoute.defaultCurrency}
                    onChange={(e) => data.setNewRoute(prev => ({ ...prev, defaultCurrency: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="USD"
                    maxLength={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sembol</label>
                  <input
                    type="text"
                    value={data.newRoute.currencySymbol}
                    onChange={(e) => data.setNewRoute(prev => ({ ...prev, currencySymbol: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="$"
                    maxLength={3}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">Rota ID: {data.newRoute.origin}_{data.newRoute.destination}</p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => data.setShowAddRoute(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={data.handleAddRoute}
                disabled={!data.newRoute.name || !data.newRoute.shortName || !data.newRoute.destination}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Route Confirmation Modal */}
      {data.deleteRouteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            ref={deleteRouteModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-route-modal-title"
            className="bg-white rounded-xl p-6 w-full max-w-md"
          >
            <h3 id="delete-route-modal-title" className="text-lg font-semibold mb-4 text-red-600">Rotay&#x131; Sil</h3>
            <p className="text-slate-600 mb-6">
              &quot;{data.routes.find(r => r.routeId === data.deleteRouteConfirm)?.name}&quot; rotas&#x131;n&#x131; ve t&#xFC;m tarifelerini silmek istedi&#x11F;inize emin misiniz?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => data.setDeleteRouteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                &#x130;ptal
              </button>
              <button
                onClick={() => data.handleDeleteRoute(data.deleteRouteConfirm!)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carrier Routes Management Modal */}
      {data.showCarrierRoutes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            ref={carrierRoutesModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="carrier-routes-modal-title"
            className="bg-white rounded-xl p-6 w-full max-w-lg"
          >
            <h3 id="carrier-routes-modal-title" className="text-lg font-semibold mb-4">
              {data.carriers.find(c => c.id === data.showCarrierRoutes)?.name} - Rota Ayarlar&#x131;
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Bu firma i&#xE7;in aktif rotalar&#x131; se&#xE7;in. Sadece se&#xE7;ili rotalar tabloda g&#xF6;r&#xFC;n&#xFC;r.
            </p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.routes.map(route => {
                const carrier = data.carriers.find(c => c.id === data.showCarrierRoutes);
                if (!carrier) return null;
                const isEnabled = !carrier.enabledRoutes || carrier.enabledRoutes.length === 0 || carrier.enabledRoutes.includes(route.routeId);
                return (
                  <label
                    key={route.routeId}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isEnabled ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => data.toggleCarrierRoute(carrier, route.routeId)}
                      className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-lg">{route.flag}</span>
                    <div className="flex-1">
                      <span className="font-medium">{route.name}</span>
                      <span className="text-sm text-slate-500 ml-2">({route.routeId})</span>
                    </div>
                    {isEnabled ? (
                      <ToggleRight className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-slate-400" />
                    )}
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => data.setShowCarrierRoutes(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carrier Rate Cards */}
      <div className="space-y-4">
        {data.carriers.filter(c => c.isActive).map(carrier => (
          <CarrierRateCard
            key={carrier.id}
            carrier={carrier}
            carrierRoutes={data.getCarrierRoutes(carrier)}
            allRoutes={data.routes}
            isExpanded={data.expandedCarriers.has(carrier.id)}
            visibleDesi={data.visibleDesi}
            getRate={data.getRate}
            updateRate={data.updateRate}
            isPriorityCarrier={data.isPriorityCarrier}
            setPriorityCarrier={data.setPriorityCarrier}
            updateCarrierCurrency={data.updateCarrierCurrency}
            onToggleExpand={() => data.toggleCarrier(carrier.id)}
            onDelete={() => data.setDeleteConfirm(carrier.id)}
            onShowRoutes={() => data.setShowCarrierRoutes(carrier.id)}
          />
        ))}
      </div>

      {/* Help Text */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600">
        <strong>{t.shipping.tipTitle}:</strong> Her kargo firmas&#x131; i&#xE7;in ayr&#x131; tablo. Y&#x131;ld&#x131;z ile i&#x15F;aretli firma &#xF6;ncelikli olarak se&#xE7;ilir.
        Excel dosyas&#x131; ile toplu y&#xFC;kleme yapabilirsiniz (her sayfa bir firma).
      </div>
    </div>
  );
};

export default ShippingPage;
